import React, { useMemo, useState, useRef, useEffect } from "react";
import { useLanguage } from "../i18n/LanguageContext";
import ReminderPanel, { ReminderWarning, reminderPayloadKontrolEt } from "../components/ReminderPanel";
import { appAlert, appConfirm } from "../components/AppDialog";
import AvatarImg from "../components/AvatarImg";
import AsyncActionButton from "../components/AsyncActionButton";
import takvimAsset from "../assets/takvimasset.png";
import { takvimKategorileri } from "../utils/constants";
import { createExternalImportKey } from "../services/externalCalendarService";

const tarihKey = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;

function CalendarPage({
  uyeler = [], gorevler = [], takvimEtkinlikleri = [], takvimRenkleri = {},
  familyId, user, tamAd, permissions,
  takvimEtkinlikEkleService, takvimEtkinlikSilService, takvimEtkinlikGuncelleService,
  onReminderKur,
  isPremium = false, premiumConfig = {}, onPremiumLimitHit,
  isBgInstance = false,
  externalCalendarEvents = [],
  onBirthdayClick,
}) {
  const { t, tList, lang } = useLanguage();
  const [importingExternalId, setImportingExternalId] = useState("");
  const [importedExternalIds, setImportedExternalIds] = useState(() => new Set());

  // API yenilenirken veya eski kayıtlarda tek bir null/bozuk öğe bulunması
  // tüm Takvim sayfasını düşürmemeli. Render boyunca yalnızca güvenli diziler kullanılır.
  const safeUyeler = useMemo(
    () => Array.isArray(uyeler) ? uyeler.filter((uye) => uye && typeof uye === "object") : [],
    [uyeler]
  );
  const safeGorevler = useMemo(
    () => Array.isArray(gorevler) ? gorevler.filter((gorev) => gorev && typeof gorev === "object") : [],
    [gorevler]
  );
  const safeTakvimEtkinlikleri = useMemo(
    () => Array.isArray(takvimEtkinlikleri) ? takvimEtkinlikleri.filter((etkinlik) => etkinlik && typeof etkinlik === "object") : [],
    [takvimEtkinlikleri]
  );
  const importedKeys = useMemo(() => new Set(safeTakvimEtkinlikleri.map((event) => event.externalImportKey).filter(Boolean)), [safeTakvimEtkinlikleri]);
  const safeExternalCalendarEvents = useMemo(
    () => Array.isArray(externalCalendarEvents) ? externalCalendarEvents.filter((event) => event && typeof event === "object" && !importedKeys.has(event.externalImportKey)) : [],
    [externalCalendarEvents, importedKeys]
  );
  const safeTakvimRenkleri = useMemo(
    () => Array.isArray(takvimRenkleri) && takvimRenkleri.length
      ? takvimRenkleri.filter((renk) => typeof renk === "string" && renk.trim())
      : ["#2563eb"],
    [takvimRenkleri]
  );

  // Backend veya eski kayıtlar tarih alanını null, Date ya da ISO saatli biçimde
  // gönderebilir. Takvim render'ı tek bir bozuk kayıt yüzünden tamamen düşmemeli.
  const normalizeDateKey = (value) => {
    if (!value) return "";
    if (value instanceof Date && !Number.isNaN(value.getTime())) return tarihKey(value);
    const raw = String(value).trim();
    const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) return "";
    const [, y, m, d] = match;
    const test = new Date(Number(y), Number(m) - 1, Number(d));
    if (
      Number.isNaN(test.getTime()) ||
      test.getFullYear() !== Number(y) ||
      test.getMonth() !== Number(m) - 1 ||
      test.getDate() !== Number(d)
    ) return "";
    return `${y}-${m}-${d}`;
  };

  const dateKeyToLocalDate = (value) => {
    const key = normalizeDateKey(value);
    if (!key) return null;
    const [year, month, day] = key.split("-").map(Number);
    return new Date(year, month - 1, day, 12, 0, 0, 0);
  };

  const addDaysToDateKey = (value, days) => {
    const date = dateKeyToLocalDate(value);
    if (!date) return "";
    date.setDate(date.getDate() + Number(days || 0));
    return tarihKey(date);
  };

  const oneMonthEndDate = (value) => {
    const date = dateKeyToLocalDate(value);
    if (!date) return "";
    const anchorDay = date.getDate();
    const targetMonthFirst = new Date(date.getFullYear(), date.getMonth() + 1, 1, 12);
    const targetMonthLastDay = new Date(targetMonthFirst.getFullYear(), targetMonthFirst.getMonth() + 1, 0).getDate();
    if (anchorDay > targetMonthLastDay) {
      return tarihKey(new Date(targetMonthFirst.getFullYear(), targetMonthFirst.getMonth(), targetMonthLastDay, 12));
    }
    const nextMonthSameDay = new Date(targetMonthFirst.getFullYear(), targetMonthFirst.getMonth(), Math.min(anchorDay, targetMonthLastDay), 12);
    nextMonthSameDay.setDate(nextMonthSameDay.getDate() - 1);
    return tarihKey(nextMonthSameDay);
  };

  const eventEndDateForSelection = (startDate) => {
    if (yeniSureTipi === "days3") return addDaysToDateKey(startDate, 2);
    if (yeniSureTipi === "days7") return addDaysToDateKey(startDate, 6);
    if (yeniSureTipi === "days10") return addDaysToDateKey(startDate, 9);
    if (yeniSureTipi === "month") return oneMonthEndDate(startDate);
    if (yeniSureTipi === "custom") return normalizeDateKey(yeniSureBitis || startDate);
    return startDate;
  };

  const listOr = (value, fallback) => Array.isArray(value) && value.length ? value : fallback;
  const GUNLER = listOr(tList("calendar.days"), ["Pazartesi", "Salı", "Çarşamba", "Perşembe", "Cuma", "Cumartesi", "Pazar"]);
  const GUNLER_K = listOr(tList("calendar.daysShort"), ["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"]);
  const AYLAR = listOr(tList("common.months"), ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"]);
  const kategoriEtiketleri = listOr(tList("const.calendarCategories"), takvimKategorileri);
  const kategoriSecenekleri = takvimKategorileri.map((value, index) => ({
    value,
    label: kategoriEtiketleri[index] || value,
  }));

  const CALENDAR_CATEGORY_TRANSLATIONS = {
    "Randevu": "Appointment",
    "Aile Etkinliği": "Family Event",
    "İş / Toplantı": "Work / Meeting",
    "Fatura / Ödeme": "Bill / Payment",
    "Okul / Çocuk": "School / Children",
    "Eğitim / Kurs": "Education / Course",
    "Sağlık": "Health",
    "Health": "Health",
    "Alışveriş": "Shopping",
    "Ev İşleri": "Household Chores",
    "Sosyal Buluşma": "Social Gathering",
    "Spor / Aktivite": "Sports / Activity",
    "Seyahat": "Travel",
    "Tatil": "Vacation",
    "Resmi İş": "Official Business",
    "Araç": "Vehicle",
    "Doğum Günü": "Birthday",
    "Kutlama": "Celebration",
    "Önemli": "Important",
    "Diğer": "Other",
    "Görev": "Task",
    "Randevu / Kontrol": "Appointment / Checkup",
  };

  const calendarCategoryLabel = (category, canonicalCategory = "") => {
    const raw = canonicalCategory || category;
    if (!raw) return "";

    const canonical = calendarCategoryCanonical(raw);
    const currentOption = kategoriSecenekleri.find((option) => option.value === canonical);
    if (currentOption) return currentOption.label;

    if (lang === "en") {
      return CALENDAR_CATEGORY_TRANSLATIONS[raw] || CALENDAR_CATEGORY_TRANSLATIONS[category] || category || raw;
    }

    const reverse = Object.entries(CALENDAR_CATEGORY_TRANSLATIONS).find(([, enLabel]) => enLabel === raw || enLabel === category);
    return reverse ? reverse[0] : raw;
  };


  const calendarCategoryCanonical = (category) => {
    if (!category) return "";
    const reverse = Object.entries(CALENDAR_CATEGORY_TRANSLATIONS).find(([, enLabel]) => enLabel === category);
    return reverse ? reverse[0] : category;
  };

  const calText = {
    allFamily: lang === "en" ? "All family" : "Tüm aile",
    allFamilyDesc: lang === "en" ? "Creates this event separately for every family member." : "Bu etkinliği her aile üyesi için ayrı ayrı ekler.",
    selected: lang === "en" ? "selected" : "seçili",
    assigneeClosedHint: lang === "en" ? "Tap to choose family members" : "Aile üyelerini seçmek için dokun",
    assigneeSelectTitle: lang === "en" ? "Who is this for?" : "Kimler için eklensin?",
    collapse: lang === "en" ? "Close" : "Kapat",
    chooseAtLeastOne: lang === "en" ? "Please select at least one family member." : "Lütfen en az bir aile üyesi seçin.",
    personal: lang === "en" ? "Personal" : "Kişisel",
    roleFather: lang === "en" ? "Father" : "Baba",
    roleMother: lang === "en" ? "Mother" : "Anne",
    roleChild: lang === "en" ? "Child" : "Çocuk",
    roleUncle: lang === "en" ? "Uncle" : "Amca",
    roleAuntPaternal: lang === "en" ? "Paternal aunt" : "Hala",
    roleAuntMaternal: lang === "en" ? "Aunt" : "Teyze",
    roleMaternalUncle: lang === "en" ? "Maternal uncle" : "Dayı",
    roleGrandmother: lang === "en" ? "Grandmother" : "Nene",
    roleGrandfather: lang === "en" ? "Grandfather" : "Dede",
    roleOther: lang === "en" ? "Other" : "Diğer",
    todayAction: lang === "en" ? "Today" : "Bugün",
    addEventAction: lang === "en" ? "Add event" : "Etkinlik ekle",
    dayEventsCount: lang === "en" ? "events" : "etkinlik",
    noEventsHint: lang === "en" ? "There are no events for this day yet." : "Bu gün için henüz etkinlik yok.",
    recurrence: lang === "en" ? "Repeat event" : "Etkinliği tekrarla",
    duration: lang === "en" ? "Event duration" : "Etkinlik süreci",
    durationSingle: lang === "en" ? "Selected day" : "Seçilen gün",
    duration3: lang === "en" ? "3 days" : "3 gün",
    duration7: lang === "en" ? "7 days" : "7 gün",
    duration10: lang === "en" ? "10 days" : "10 gün",
    durationMonth: lang === "en" ? "1 month" : "1 ay",
    durationCustom: lang === "en" ? "Date range" : "Tarih aralığı",
    rangeStart: lang === "en" ? "Start" : "Başlangıç",
    rangeEnd: lang === "en" ? "End" : "Bitiş",
    invalidRange: lang === "en" ? "The end date cannot be earlier than the start date." : "Bitiş tarihi başlangıç tarihinden önce olamaz.",
    ongoing: lang === "en" ? "Ongoing" : "Devam eden",
    processEnds: lang === "en" ? "Ends" : "Bitiş",
    recurrenceNone: lang === "en" ? "Does not repeat" : "Tekrarlanmaz",
    recurrenceWeekly: lang === "en" ? "Weekly" : "Haftalık",
    recurrenceMonthly: lang === "en" ? "Monthly" : "Aylık",
    recurrenceQuarterly: lang === "en" ? "Every 3 months" : "3 ayda bir",
    recurrenceSemiannual: lang === "en" ? "Every 6 months" : "6 ayda bir",
    recurrenceYearly: lang === "en" ? "Yearly" : "Yıllık",
    recurrenceLimit: lang === "en" ? "Occurrences are created for up to 5 years." : "Tekrarlar en fazla 5 yıl ileriye oluşturulur.",
    deleteSeriesQuestion: lang === "en" ? "Delete the entire recurring series?" : "Tekrarlanan kayıtların tamamı silinsin mi?",
    deleteSeriesAll: lang === "en" ? "Delete series" : "Tümünü sil",
    deleteOnlyThis: lang === "en" ? "Only this event" : "Yalnız bu kayıt",
    deleteOnlyConfirm: lang === "en" ? "Delete only this occurrence?" : "Yalnızca bu kayıt silinsin mi?",
  };

  const normalizeRole = (u = {}) => {
    const raw = String(
      u.familyRole || u.family_role || u.aileRolu || u.aile_rolu ||
      u.rol || u.role || u.yakinlik || u.relationship || u.unvan || u.title || ""
    ).toLocaleLowerCase("tr-TR").trim();

    // Kişinin kendi kaydı olsa bile gerçek aile rolü varsa onu göster.
    // Önceki yapı mevcut kullanıcıyı doğrudan "Kişisel" yaptığı için Baba/Anne gibi roller eziliyordu.
    if (raw.includes("baba") || raw.includes("father") || raw === "dad") return "father";
    if (raw.includes("anne") || raw.includes("mother") || raw === "mom") return "mother";
    if (raw.includes("çocuk") || raw.includes("cocuk") || raw.includes("child") || raw.includes("kız") || raw.includes("kiz") || raw.includes("erkek")) return "child";
    if (raw.includes("amca")) return "uncle";
    if (raw.includes("hala")) return "auntPaternal";
    if (raw.includes("teyze") || raw === "aunt") return "auntMaternal";
    if (raw.includes("dayı") || raw.includes("dayi")) return "maternalUncle";
    if (raw.includes("nene") || raw.includes("nine") || raw.includes("babaanne") || raw.includes("anneanne") || raw.includes("grandmother")) return "grandmother";
    if (raw.includes("dede") || raw.includes("grandfather")) return "grandfather";
    if (raw.includes("kişisel") || raw.includes("kisisel") || raw.includes("personal") || raw === "me") return "personal";
    return "other";
  };

  const roleOrder = { personal: 0, father: 1, mother: 2, child: 3, uncle: 4, auntPaternal: 5, auntMaternal: 6, maternalUncle: 7, grandmother: 8, grandfather: 9, other: 10 };
  const roleLabel = (role) => ({
    personal: calText.personal,
    father: calText.roleFather,
    mother: calText.roleMother,
    child: calText.roleChild,
    uncle: calText.roleUncle,
    auntPaternal: calText.roleAuntPaternal,
    auntMaternal: calText.roleAuntMaternal,
    maternalUncle: calText.roleMaternalUncle,
    grandmother: calText.roleGrandmother,
    grandfather: calText.roleGrandfather,
    other: calText.roleOther,
  }[role] || calText.roleOther);

  const siraliUyeler = useMemo(() => [...safeUyeler].sort((a, b) => {
    const ra = normalizeRole(a);
    const rb = normalizeRole(b);
    const rank = (roleOrder[ra] ?? 99) - (roleOrder[rb] ?? 99);
    if (rank !== 0) return rank;
    return `${a.ad || ""} ${a.soyad || ""}`.localeCompare(`${b.ad || ""} ${b.soyad || ""}`, lang === "en" ? "en" : "tr");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [safeUyeler, user?.uid, lang]);

  const okunakli = (str) => {
    if (!str) return "";
    const [y, m, d] = str.split("-");
    return `${parseInt(d)} ${AYLAR[parseInt(m)-1]} ${y}`;
  };

  const [tarih, setTarih]               = useState(new Date());
  const [seciliGun, setSeciliGun]       = useState(null);

  // Aile veya hesap değişince açık modal/seçili gün sıfırlansın
  useEffect(() => { setSeciliGun(null); setFormAcik(false); setDuzenle(null); setYeniSureTipi("single"); setYeniSureBaslangic(""); setYeniSureBitis(""); }, [familyId, user?.uid]); // eslint-disable-line react-hooks/exhaustive-deps
  const [formAcik, setFormAcik]         = useState(false);
  const [duzenle, setDuzenle]           = useState(null);
  const [yillikGorunum, setYillikGorunum] = useState(false);

  const ilkKategori = "Aile Etkinliği";
  const [yeniBaslik, setYeniBaslik]     = useState("");
  const [yeniSaat, setYeniSaat]         = useState("");
  const [yeniKategori, setYeniKategori] = useState(ilkKategori);
  const [yeniSureTipi, setYeniSureTipi] = useState("single");
  const [yeniSureBaslangic, setYeniSureBaslangic] = useState("");
  const [yeniSureBitis, setYeniSureBitis] = useState("");
  const [yeniTekrar, setYeniTekrar]     = useState("none");
  const [yeniKisiler, setYeniKisiler]   = useState([]);
  const [kisiSecAcik, setKisiSecAcik]   = useState(false);
  const [etkinlikKaydediliyor, setEtkinlikKaydediliyor] = useState(false);
  const [dBaslik, setDBaslik]           = useState("");
  const [dSaat, setDSaat]               = useState("");
  const [dEndDate, setDEndDate]         = useState("");
  const [dKategori, setDKategori]       = useState(ilkKategori);
  const [dKisi, setDKisi]               = useState("");

  const [reminderAktif,  setReminderAktif]  = useState(false);
  const [reminderTarih,  setReminderTarih]  = useState("");
  const [reminderSaat,   setReminderSaat]   = useState("09:00");
  const [reminderTekrar, setReminderTekrar] = useState("none");
  const [reminderError, setReminderError] = useState("");

  const [monthTransition, setMonthTransition] = useState("");
  const monthSwipeRef = useRef({ startX: 0, startY: 0, active: false });

  const ayDegistir = (delta) => {
    const nextDirection = delta > 0 ? "next" : "prev";
    setMonthTransition(nextDirection);
    setTarih(prev => new Date(prev.getFullYear(), prev.getMonth() + delta, 1));
    window.setTimeout(() => setMonthTransition(""), 360);
  };

  const handleMonthSwipeStart = (e) => {
    const touch = e.touches?.[0];
    if (!touch) return;
    monthSwipeRef.current = {
      startX: touch.clientX,
      startY: touch.clientY,
      active: true,
    };
  };

  const handleMonthSwipeEnd = (e) => {
    if (!monthSwipeRef.current.active) return;
    const touch = e.changedTouches?.[0];
    if (!touch) return;

    const dx = touch.clientX - monthSwipeRef.current.startX;
    const dy = touch.clientY - monthSwipeRef.current.startY;
    monthSwipeRef.current.active = false;

    if (Math.abs(dx) < 52 || Math.abs(dx) < Math.abs(dy) * 1.15) return;

    if (dx < 0) {
      ayDegistir(1);
    } else {
      ayDegistir(-1);
    }
  };

  const yil = tarih.getFullYear(), ay = tarih.getMonth();
  const ilkGun  = new Date(yil, ay, 1);
  const toplamGun = new Date(yil, ay+1, 0).getDate();
  const baslangic = (ilkGun.getDay()+6)%7;
  const bugun = new Date();

  const renkMap = useMemo(() => {
    const m = {};
    safeUyeler.forEach((u, i) => { m[u.uid] = u.calendarColor || safeTakvimRenkleri[i % safeTakvimRenkleri.length]; });
    return m;
  }, [safeUyeler, safeTakvimRenkleri]);

  const ALL_FAMILY_CALENDAR_UID = "__all_family__";
  const ALL_FAMILY_CALENDAR_COLOR = "#8b5cf6";
  const isAllFamilyUid = (uid) => uid === ALL_FAMILY_CALENDAR_UID || uid === "all_family" || uid === "tum_aile" || uid === "__all__";
  const isAllFamilyLabel = (label = "") => ["tüm aile", "tum aile", "all family"].includes(String(label).toLocaleLowerCase("tr-TR").trim());

  const renk = (uid) => isAllFamilyUid(uid) ? ALL_FAMILY_CALENDAR_COLOR : (renkMap[uid] || "#2563eb");

  const etkinlikRengi = (e = {}) => {
    const allFamily = isAllFamilyUid(e.ilgiliKisiUid || e.kisiUid) || isAllFamilyLabel(e.ilgiliKisiAd || e.kisiAd);
    if (allFamily) return ALL_FAMILY_CALENDAR_COLOR;

    // Renkler kayıt oluşturulduğu andaki sabit renkten değil, her render'da güncel aile üyesi renginden çözülür.
    // Böylece kullanıcı profil rengini değiştirdiğinde eski takvim etkinlikleri de otomatik güncel renge döner.
    const candidateUids = [
      e.ilgiliKisiUid,
      e.kisiUid,
      e.userUid,
      e.uid,
      e.ekleyenUid,
      e.createdByUid,
      e.created_by_uid,
      e.ownerUid,
    ].map((v) => String(v || "")).filter(Boolean);

    const dynamicUid = candidateUids.find((uid) => !isAllFamilyUid(uid) && renkMap[uid]);
    return dynamicUid ? renk(dynamicUid) : (e.renk || "#2563eb");
  };

  const seciliUyeler = useMemo(() => siraliUyeler.filter(u => yeniKisiler.includes(u.uid)), [siraliUyeler, yeniKisiler]);

  const kisiSecimOzeti = useMemo(() => {
    if (!yeniKisiler.length) return calText.assigneeClosedHint;
    if (siraliUyeler.length > 0 && yeniKisiler.length === siraliUyeler.length) return calText.allFamily;
    if (seciliUyeler.length === 1) return `${seciliUyeler[0].ad || ""} ${seciliUyeler[0].soyad || ""}`.trim();
    return `${yeniKisiler.length} ${calText.selected}`;
  }, [yeniKisiler, siraliUyeler.length, seciliUyeler, calText.assigneeClosedHint, calText.allFamily, calText.selected]);

  const UyeAvatar = ({ uye, small = false }) => (
    <AvatarImg
      avatar={uye?.avatar || null}
      renk={renk(uye?.uid)}
      initials={`${uye?.ad || "?"}`.charAt(0).toUpperCase()}
      size={small ? 24 : 34}
      radius={small ? 10 : 13}
    />
  );

  const dogumGunleri = useMemo(() => {
    const events = [];
    const buYil = new Date().getFullYear();
    safeUyeler.forEach(u => {
      try {
        const dogumTarihi = normalizeDateKey(u.dogumTarihi);
        if (!dogumTarihi) return;
        const [, mm, dd] = dogumTarihi.split("-");
        [buYil, buYil + 1].forEach(y => {
          const leapDay = mm === "02" && dd === "29";
          const leapYear = y % 4 === 0 && (y % 100 !== 0 || y % 400 === 0);
          const tarihStr = `${y}-${mm}-${leapDay && !leapYear ? "28" : dd}`;
          events.push({
            id: `birthday-${u.uid}-${y}`,
            type: "birthday",
            baslik: `🎂 ${u.ad} ${u.soyad}`,
            tarih: tarihStr, saat: "",
            kategori: t("notif.type.birthday"),
            kisiUid: u.uid, kisiUserId: u.userId || "", kisiAd: `${u.ad} ${u.soyad}`,
            ekleyenUid: null,
            renk: u.calendarColor || renk(u.uid),
            tamamlandi: false,
          });
        });
      } catch (e) {
        console.warn("[CalendarPage] Doğum günü eklenemedi, atlandı:", u?.uid, e?.message);
      }
    });
    return events;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [safeUyeler, renkMap]);

  const uyeId = (u = {}) => String(u.uid || u.id || "");
  const uyeAd = (u = {}) => `${u.ad || u.name || ""} ${u.soyad || u.surname || ""}`.trim() || u.displayName || u.email || "";
  const uyeBul = (uid) => {
    const id = String(uid || "");
    if (!id) return null;
    return safeUyeler.find((u) => uyeId(u) === id) || null;
  };

  const gorevTakvimGorunum = (g = {}) => {
    const atananUids = Array.isArray(g.atananUids) ? g.atananUids.map(String).filter(Boolean) : [];
    const tumAileMi = Boolean(
      g.atananScope === "all_family" ||
      g.assigneeScope === "all_family" ||
      g.isAllFamily === true ||
      (siraliUyeler.length > 1 && atananUids.length >= siraliUyeler.length && siraliUyeler.every((u) => atananUids.includes(uyeId(u))))
    );

    if (tumAileMi) {
      return {
        kisiUid: ALL_FAMILY_CALENDAR_UID,
        kisiAd: calText.allFamily,
        renk: ALL_FAMILY_CALENDAR_COLOR,
      };
    }

    // Görev birden fazla kişiye atanmış olsa bile takvim kartı rastgele atanan kişi adıyla değil,
    // görevi oluşturan kişi adı ve onun güncel takvim rengiyle gösterilir.
    const olusturanUid = String(g.ekleyenUid || g.createdByUid || g.created_by_uid || g.ownerUid || "");
    const olusturanUye = uyeBul(olusturanUid);
    const olusturanAd = uyeAd(olusturanUye) || g.ekleyen || g.createdByName || g.created_by_name || tamAd || "";

    return {
      kisiUid: olusturanUid,
      kisiAd: olusturanAd,
      renk: renk(olusturanUid),
    };
  };

  const tumKayitlar = useMemo(() => [
    ...safeTakvimEtkinlikleri.reduce((acc, e) => {
      // Bozuk takvim etkinliği tüm render'ı çökertmesin diye try/catch ile korunuyor.
      try {
        acc.push({
          id: e.id,
          type: "event",
          baslik: e.baslik || "",
          tarih: normalizeDateKey(e.tarih),
          endDate: normalizeDateKey(e.endDate || e.bitisTarihi || e.tarih) || normalizeDateKey(e.tarih),
          durationDays: Number.isInteger(e.durationDays) ? e.durationDays : 0,
          saat: e.saat || "",
          kategori: e.kategori,
          kategoriCanonical: e.kategoriCanonical || e.kategori,
          kisiUid: (!e.ilgiliKisiUid && isAllFamilyLabel(e.ilgiliKisiAd)) ? ALL_FAMILY_CALENDAR_UID : e.ilgiliKisiUid,
          kisiAd: (!e.ilgiliKisiUid && isAllFamilyLabel(e.ilgiliKisiAd)) ? calText.allFamily : e.ilgiliKisiAd,
          ekleyenUid: e.ekleyenUid,
          renk: etkinlikRengi(e),
          tamamlandi: false,
          recurrenceType: e.recurrenceType || e.recurrence_type || "none",
          recurrenceSeriesId: e.recurrenceSeriesId || e.recurrence_series_id || null,
          recurrenceIndex: e.recurrenceIndex ?? e.recurrence_index ?? null,
          recurrenceUntil: e.recurrenceUntil || e.recurrence_until || null,
          externalImportKey: e.externalImportKey || null,
        });
      } catch (e2) {
        console.warn("[CalendarPage] Takvim etkinliği işlenemedi, atlandı:", e?.id, e2?.message);
      }
      return acc;
    }, []),
    ...safeExternalCalendarEvents.map((event) => ({
      ...event,
      type: "externalEvent",
      tarih: normalizeDateKey(event.tarih),
      endDate: normalizeDateKey(event.endDate || event.tarih) || normalizeDateKey(event.tarih),
      renk: event.renk || event.sourceColor || "#64748b",
      tamamlandi: false,
    })),
    ...safeGorevler.filter(g => g && (g.sonTarih || g.dueDate || g.son_tarih)).reduce((acc, g) => {
      // Bozuk görev kaydı tüm takvim render'ını çökertmesin diye try/catch ile korunuyor.
      try {
        const gorunum = gorevTakvimGorunum(g);
        acc.push({
          id: g.id || g.firebaseId || g._id,
          type: "task",
          baslik: g.ad || g.baslik || g.title || "",
          tarih: normalizeDateKey(g.sonTarih || g.dueDate || g.son_tarih),
          saat: g.saat || g.time || "",
          kategori: "Görev",
          kisiUid: gorunum.kisiUid,
          kisiAd: gorunum.kisiAd,
          ekleyenUid: g.ekleyenUid,
          renk: gorunum.renk,
          tamamlandi: g.tamamlandi,
        });
      } catch (e) {
        console.warn("[CalendarPage] Görev takvime eklenemedi, atlandı:", g?.id, e?.message);
      }
      return acc;
    }, []),
    ...dogumGunleri,
  ].filter((kayit) => kayit && kayit.tarih),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [safeTakvimEtkinlikleri, safeExternalCalendarEvents, safeGorevler, renkMap, dogumGunleri, safeUyeler, siraliUyeler, calText.allFamily, tamAd]);

  const kayitGunundeMi = (kayit, dateKey) => {
    const startDate = normalizeDateKey(kayit?.tarih);
    const endDate = normalizeDateKey(kayit?.endDate || startDate);
    return Boolean(startDate && dateKey >= startDate && dateKey <= (endDate || startDate));
  };

  const ayBaslangicKey = tarihKey(new Date(yil, ay, 1));
  const ayBitisKey = tarihKey(new Date(yil, ay + 1, 0));
  const mevcutAyKayitlari = tumKayitlar.filter((kayit) => {
    const startDate = normalizeDateKey(kayit?.tarih);
    const endDate = normalizeDateKey(kayit?.endDate || startDate);
    return startDate && startDate <= ayBitisKey && (endDate || startDate) >= ayBaslangicKey;
  });

  const gunKayitlari = (g) => {
    const k = tarihKey(new Date(yil, ay, g));
    return mevcutAyKayitlari.filter(x => kayitGunundeMi(x, k));
  };

  const bugunT = new Date(); bugunT.setHours(0,0,0,0);
  const yarinT = new Date(bugunT); yarinT.setDate(yarinT.getDate()+1);
  const haftaT = new Date(bugunT); haftaT.setDate(haftaT.getDate()+7); haftaT.setHours(23,59,59,999);
  const bugunK = tarihKey(bugunT), yarinK = tarihKey(yarinT);

  const bugunKayit = tumKayitlar.filter(k => kayitGunundeMi(k, bugunK));
  const yarinKayit = tumKayitlar.filter(k => kayitGunundeMi(k, yarinK));
  const haftaKayit = tumKayitlar.filter(k => { const d = new Date(k.tarih); d.setHours(0,0,0,0); return d > yarinT && d <= haftaT; });

  const kaydaGit = (kayit) => {
    if (kayit?.type === "birthday" && typeof onBirthdayClick === "function") {
      setSeciliGun(null);
      setFormAcik(false);
      setDuzenle(null);
      onBirthdayClick(kayit);
      return;
    }
    const guvenliTarih = normalizeDateKey(kayit?.tarih);
    if (!guvenliTarih) return;
    const [y2, m2, d2] = guvenliTarih.split("-");
    setTarih(new Date(+y2, +m2-1, 1));
    setSeciliGun({ tarih: guvenliTarih, gun: +d2, kayitlar: tumKayitlar.filter(k => kayitGunundeMi(k, guvenliTarih)) });
    setFormAcik(false); setDuzenle(null);
  };

  const etkinlikKaydet = async () => {
    if (etkinlikKaydediliyor) return;
    if (!yeniBaslik.trim()) return appAlert(t("calendar.titleRequired"));
    const secilenUidler = yeniKisiler.length > 0 ? yeniKisiler : [];
    const secilenUyeler = secilenUidler.map(uid => safeUyeler.find(u => u.uid === uid)).filter(Boolean);
    if (secilenUyeler.length === 0) return appAlert(calText.chooseAtLeastOne);
    const baslangicTarihi = yeniSureTipi === "custom"
      ? normalizeDateKey(yeniSureBaslangic || seciliGun.tarih)
      : normalizeDateKey(seciliGun.tarih);
    const bitisTarihi = eventEndDateForSelection(baslangicTarihi);
    if (!baslangicTarihi || !bitisTarihi || bitisTarihi < baslangicTarihi) return appAlert(calText.invalidRange);
    const tumAileSecili = siraliUyeler.length > 0 && secilenUidler.length === siraliUyeler.length;
    const olusacakKayitSayisi = tumAileSecili ? 1 : secilenUyeler.length;
    if (!isPremium) {
      const now = new Date();
      const buAyEtkinlik = safeTakvimEtkinlikleri.filter(e => {
        if (!e.tarih) return false;
        const d = new Date(e.tarih);
        return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
      }).length;
      const maxEtkinlik = premiumConfig?.limits?.aylikTakvim ?? 10;
      if (buAyEtkinlik + olusacakKayitSayisi > maxEtkinlik) {
        onPremiumLimitHit && onPremiumLimitHit(
          t("calendar.upcoming"),
          buAyEtkinlik,
          maxEtkinlik,
          [t("calendar.monthlyLimitDesc"), t("calendar.premium.recurringEvents"), t("calendar.premium.familySync"), t("premium.unlimitedAllModules")]
        );
        return;
      }
    }
    let reminderPayload = null;
    if (reminderAktif && onReminderKur) {
      const bazTarih = reminderTarih || baslangicTarihi;
      reminderPayload = reminderPayloadKontrolEt({ mod: "datetime", tarih: bazTarih, saat: reminderSaat, tekrar: reminderTekrar }, t, lang, setReminderError);
      if (!reminderPayload) return;
    }

    setEtkinlikKaydediliyor(true);
    try {
      const kategoriCanonical = calendarCategoryCanonical(yeniKategori);
      const olusanKayitlar = [];
      const kayitHedefleri = tumAileSecili
        ? [{ uid: ALL_FAMILY_CALENDAR_UID, ad: calText.allFamily, soyad: "", allFamily: true }]
        : secilenUyeler;

      for (const kisi of kayitHedefleri) {
        const kisiAd = kisi.allFamily ? calText.allFamily : `${kisi.ad} ${kisi.soyad}`.trim();
        const kisiRenk = kisi.allFamily ? ALL_FAMILY_CALENDAR_COLOR : renk(kisi.uid);
        const olusturma = await takvimEtkinlikEkleService({
          familyId, baslik: yeniBaslik, aciklama: "", tarih: baslangicTarihi, endDate: bitisTarihi,
          saat: yeniSaat, kategori: kategoriCanonical,
          ilgiliKisiUid: kisi.allFamily ? "" : kisi.uid, ilgiliKisiAd: kisiAd,
          renk: kisiRenk, tamAd, user,
          hedefTipi: kisi.allFamily ? "all_family" : "member",
          hedefUidler: kisi.allFamily ? secilenUidler : [kisi.uid],
          recurrenceType: yeniTekrar,
        });

        const servisKayitlari = Array.isArray(olusturma?.events) ? olusturma.events : [];
        if (servisKayitlari.length) {
          olusanKayitlar.push(...servisKayitlari.map((event) => ({
            ...event,
            id: event.id || event.firebaseId,
            type: "event",
            kisiUid: kisi.allFamily ? ALL_FAMILY_CALENDAR_UID : (event.ilgiliKisiUid || kisi.uid),
            kisiAd: event.ilgiliKisiAd || kisiAd,
            renk: event.renk || kisiRenk,
          })));
        } else olusanKayitlar.push({
          id: olusturma?.id || `temp-${Date.now()}-${kisi.uid}`,
          type: "event", baslik: yeniBaslik,
          tarih: baslangicTarihi, endDate: bitisTarihi, saat: yeniSaat,
          kategori: kategoriCanonical,
          kategoriCanonical,
          kisiUid: kisi.allFamily ? ALL_FAMILY_CALENDAR_UID : kisi.uid, kisiAd,
          ekleyenUid: user?.uid,
          renk: kisiRenk, tamamlandi: false,
          hedefTipi: kisi.allFamily ? "all_family" : "member",
          hedefUidler: kisi.allFamily ? secilenUidler : [kisi.uid],
        });
      }

      if (reminderPayload && onReminderKur) {
        const saatMetin = yeniSaat ? ` (${t("calendar.time")}: ${yeniSaat})` : "";
        const ilkEtkinlikTarihi = baslangicTarihi;
        const ilkHatirlatma = new Date(reminderPayload.planlananTarih);
        const ilkEtkinlik = new Date(`${ilkEtkinlikTarihi}T00:00:00`);
        const reminderOffsetMs = ilkHatirlatma.getTime() - ilkEtkinlik.getTime();
        for (let index = 0; index < olusanKayitlar.length; index += 6) {
          const batch = olusanKayitlar.slice(index, index + 6);
          await Promise.all(batch.map((olusan) => {
            const occurrenceDate = new Date(`${normalizeDateKey(olusan.tarih)}T00:00:00`);
            const occurrenceReminder = yeniTekrar === "none"
              ? reminderPayload.planlananTarih
              : new Date(occurrenceDate.getTime() + reminderOffsetMs);
            return onReminderKur({
            baslik: t("calendar.reminderTitle"),
            aciklama: t("notify.calendarReminder.body", { event: yeniBaslik, timeText: saatMetin }),
            baslikKey: "calendar.reminderTitle",
            aciklamaKey: "notify.calendarReminder.body",
            aciklamaVars: { event: yeniBaslik, timeText: saatMetin },
            kaynak: "calendar",
            kaynakId: olusan.id,
            kaynakTuru: "calendarEvent",
            url: "/?pushPage=takvim",
            ...reminderPayload,
            planlananTarih: occurrenceReminder,
            tekrarTipi: yeniTekrar === "none" ? reminderPayload.tekrarTipi : "none",
          }).catch(() => {});
          }));
        }
        setReminderAktif(false); setReminderTarih(""); setReminderSaat("09:00"); setReminderTekrar("none"); setReminderError("");
      }

      setSeciliGun(prev => ({
        ...prev,
        kayitlar: [
          ...(prev?.kayitlar || []),
          ...olusanKayitlar.filter((kayit) => kayitGunundeMi(kayit, prev?.tarih)),
        ],
      }));
      setYeniBaslik(""); setYeniSaat(""); setYeniKategori(ilkKategori); setYeniSureTipi("single"); setYeniSureBaslangic(""); setYeniSureBitis(""); setYeniTekrar("none"); setYeniKisiler([]); setKisiSecAcik(false); setFormAcik(false);
      return true;
    } catch (error) {
      console.error("[CalendarPage] Etkinlik kaydedilemedi:", error);
      throw error;
    } finally {
      setEtkinlikKaydediliyor(false);
    }
  };

  const silebilir = (k) => k.type === "event" && (permissions?.actions?.deleteAllCalendarEvents || (permissions?.actions?.deleteOwnCalendarEvent && k.ekleyenUid === user?.uid));
  const duzenlenebilir = (k) => k.type === "event" && (permissions?.actions?.updateAllCalendarEvents || (permissions?.actions?.updateOwnCalendarEvent && k.ekleyenUid === user?.uid));

  const hariciEtkinligiAileyeAktar = async (kayit) => {
    if (importingExternalId || kayit?.type !== "externalEvent") return;
    if (!permissions?.actions?.addCalendarEvent) {
      await appAlert(lang === "en" ? "You do not have permission to add family calendar events." : "Aile takvimine etkinlik ekleme yetkiniz yok.");
      return;
    }
    const confirmed = await appConfirm(
      lang === "en"
        ? "A copy of this device event will be uploaded to the family calendar and become visible to permitted family members. Continue?"
        : "Bu cihaz etkinliğinin bir kopyası aile takvimine yüklenecek ve yetkili aile üyeleri tarafından görülebilecek. Devam edilsin mi?",
      {
        title: lang === "en" ? "Copy to family calendar" : "Aile takvimine aktar",
        confirmText: lang === "en" ? "Copy" : "Aktar",
      }
    );
    if (!confirmed) return;
    setImportingExternalId(kayit.id);
    try {
      const externalImportKey = kayit.externalImportKey || await createExternalImportKey(kayit);
      const currentMember = safeUyeler.find((member) => member.uid === user?.uid) || safeUyeler[0] || null;
      await takvimEtkinlikEkleService({
        familyId,
        baslik: kayit.baslik,
        aciklama: kayit.aciklama || "",
        tarih: kayit.tarih,
        endDate: kayit.endDate || kayit.tarih,
        saat: kayit.allDay ? "" : (kayit.saat || ""),
        kategori: "Diğer",
        ilgiliKisiUid: currentMember?.uid || ALL_FAMILY_CALENDAR_UID,
        ilgiliKisiAd: currentMember ? uyeAd(currentMember) : calText.allFamily,
        renk: kayit.renk || "#64748b",
        hedefTipi: currentMember ? "selected" : "all_family",
        hedefUidler: currentMember?.uid ? [currentMember.uid] : safeUyeler.map((member) => member.uid),
        recurrenceType: "none",
        source: "externalCalendarImport",
        externalImportKey,
        externalProvider: kayit.provider || "device",
        externalCalendarTitle: kayit.sourceTitle || "",
        tamAd,
        user,
      });
      setImportedExternalIds((current) => new Set([...current, kayit.id]));
      await appAlert(lang === "en" ? "The event was copied to the family calendar." : "Etkinlik aile takvimine aktarıldı.");
    } catch (error) {
      if (error?.code === "EXTERNAL_EVENT_ALREADY_IMPORTED" || error?.status === 409) {
        setImportedExternalIds((current) => new Set([...current, kayit.id]));
        await appAlert(lang === "en" ? "This event was already copied to the family calendar." : "Bu etkinlik aile takvimine daha önce aktarılmış.");
      } else {
        await appAlert(error?.message || (lang === "en" ? "The event could not be copied." : "Etkinlik aktarılamadı."));
      }
    } finally {
      setImportingExternalId("");
    }
  };

  const etkinlikSil = async (kayit) => {
    if (!silebilir(kayit)) return appAlert(t("calendar.noPermDelete"));
    let scope = "single";
    const tekrarliKayit = Boolean(
      kayit.recurrenceSeriesId ||
      (kayit.recurrenceType && kayit.recurrenceType !== "none")
    );
    if (tekrarliKayit) {
      const tumSeri = await appConfirm(calText.deleteSeriesQuestion, {
        confirmText: calText.deleteSeriesAll,
        cancelText: calText.deleteOnlyThis,
      });
      if (tumSeri) scope = "series";
      else if (!await appConfirm(calText.deleteOnlyConfirm)) return;
    } else if (!await appConfirm(`${kayit.baslik} ${t("calendar.deleteConfirm")}`)) return;
    const deletedIds = await takvimEtkinlikSilService({ familyId, eventId: kayit.id, event: kayit, scope, user, tamAd });
    const deletedSet = new Set((deletedIds || [kayit.id]).map(String));
    setSeciliGun(p => ({ ...p, kayitlar: p.kayitlar.filter(k => !(k.type === "event" && deletedSet.has(String(k.id)))) }));
    setDuzenle(null);
  };

  const duzenleBaslat = (kayit) => {
    setDuzenle(kayit); setDBaslik(kayit.baslik || ""); setDSaat(kayit.saat || "");
    setDEndDate(normalizeDateKey(kayit.endDate || kayit.tarih));
    setDKategori(calendarCategoryCanonical(kayit.kategoriCanonical || kayit.kategori) || ilkKategori); setDKisi(kayit.kisiUid || ""); setFormAcik(false);
  };

  const etkinlikGuncelle = async () => {
    if (!duzenle || !dBaslik.trim()) return appAlert(t("calendar.titleRequired"));
    const duzenlemeBitis = normalizeDateKey(dEndDate || duzenle.tarih);
    if (!duzenlemeBitis || duzenlemeBitis < normalizeDateKey(duzenle.tarih)) return appAlert(calText.invalidRange);
    const allFamilyEdit = isAllFamilyUid(dKisi);
    const k = allFamilyEdit ? null : (safeUyeler.find(u => u.uid === dKisi) || safeUyeler[0]);
    if (!allFamilyEdit && !k) return appAlert(t("calendar.personNotFound"));
    const veri = {
      baslik: dBaslik.trim(),
      saat: dSaat,
      endDate: duzenlemeBitis,
      kategori: calendarCategoryCanonical(dKategori),
      kategoriCanonical: calendarCategoryCanonical(dKategori),
      ilgiliKisiUid: allFamilyEdit ? "" : k.uid,
      ilgiliKisiAd: allFamilyEdit ? calText.allFamily : `${k.ad} ${k.soyad}`,
      renk: allFamilyEdit ? ALL_FAMILY_CALENDAR_COLOR : renk(k.uid),
      hedefTipi: allFamilyEdit ? "all_family" : "member",
      hedefUidler: allFamilyEdit ? siraliUyeler.map(u => u.uid) : [k.uid],
    };
    await takvimEtkinlikGuncelleService({ familyId, eventId: duzenle.id, yeniVeri: veri, event: duzenle, user, tamAd });
    setSeciliGun(p => ({ ...p, kayitlar: p.kayitlar.map(x => x.type === "event" && x.id === duzenle.id ? { ...x, ...veri, kisiUid: allFamilyEdit ? ALL_FAMILY_CALENDAR_UID : veri.ilgiliKisiUid, kisiAd: veri.ilgiliKisiAd, renk: veri.renk } : x) }));
    setDuzenle(null); setDBaslik(""); setDSaat(""); setDEndDate(""); setDKategori(ilkKategori); setDKisi("");
  };

  const UpcItem = ({ k }) => (
    <button type="button" className="upcRow" style={{ "--event-color": k.renk }} onClick={() => kaydaGit(k)}>
      <span className="upcColorDot" aria-hidden="true" />
      <div className="upcContent">
        <span className="upcBaslik">{k.baslik}</span>
        <span className="upcMeta">
          {k.saat || (k.type === "birthday" ? t("notif.type.birthday") : calendarCategoryLabel(k.kategori, k.kategoriCanonical))}
          {k.kisiAd ? ` · ${k.kisiAd}` : ""}
        </span>
      </div>
      <span className="upcArrow" aria-hidden="true">›</span>
    </button>
  );

  // ── YILLIK GÖRÜNÜM ──
  if (yillikGorunum) {
    return (
      <div className="pageContent calPageContent">
        <div className="calHeader">
          <button className="calNavBtn" onClick={() => setTarih(new Date(yil-1, ay, 1))}>‹</button>
          <span className="calHeaderTitle calHeaderTitleClickable" onClick={() => setYillikGorunum(false)} title={t("calendar.backToMonthly")}>
            {yil} · {t("calendar.yearlyView")} ↩
          </span>
          <button className="calNavBtn" onClick={() => setTarih(new Date(yil+1, ay, 1))}>›</button>
        </div>
        <div className="calYillikGrid">
          {Array.from({ length: 12 }, (_, mi) => {
            const ayBaslangic = new Date(yil, mi, 1);
            const ayGun = new Date(yil, mi+1, 0).getDate();
            const ayBasSira = (ayBaslangic.getDay()+6) % 7;
            const prefix = `${yil}-${String(mi+1).padStart(2, "0")}-`;
            const monthStartKey = `${prefix}01`;
            const monthEndKey = `${prefix}${String(ayGun).padStart(2, "0")}`;
            const ayKayitlari = tumKayitlar.filter((kayit) => {
              const startDate = normalizeDateKey(kayit?.tarih);
              const endDate = normalizeDateKey(kayit?.endDate || startDate);
              return startDate && startDate <= monthEndKey && (endDate || startDate) >= monthStartKey;
            });
            return (
              <div key={mi} className="calYillikAy" onClick={() => { setTarih(new Date(yil, mi, 1)); setYillikGorunum(false); }}>
                <div className="calYillikAyBaslik">{AYLAR[mi]}</div>
                <div className="calYillikGunSatirlari">
                  {GUNLER_K.map((g, i) => <div key={i} className="calYillikGunHdr">{g}</div>)}
                  {Array.from({ length: ayBasSira }, (_, i) => <div key={"b"+i} className="calYillikKutucuk bos"/>)}
                  {Array.from({ length: ayGun }, (_, i) => {
                    const g = i + 1;
                    const key = `${prefix}${String(g).padStart(2, "0")}`;
                    const isBugun = key === tarihKey(new Date());
                    const gunKayit = ayKayitlari.filter(k => kayitGunundeMi(k, key));
                    const surec = gunKayit.find(k => k.type === "event" && normalizeDateKey(k.endDate) > normalizeDateKey(k.tarih));
                    const varEtkinlik = gunKayit.length > 0;
                    const haftaIndex = (new Date(yil, mi, g).getDay() + 6) % 7;
                    const surecBaslangic = surec && key === surec.tarih;
                    const surecBitis = surec && key === surec.endDate;
                    return (
                      <div key={g} className={`calYillikKutucuk ${isBugun ? "bugun" : ""} ${varEtkinlik ? "etkVar" : ""}`}>
                        {g}
                        {surec ? (
                          <span
                            className={`calYillikSurec ${(surecBaslangic || haftaIndex === 0) ? "start" : ""} ${(surecBitis || haftaIndex === 6) ? "end" : ""}`}
                            style={{ "--event-color": surec.renk }}
                            aria-label={`${surec.baslik}: ${surec.tarih} – ${surec.endDate}`}
                          />
                        ) : varEtkinlik ? <div className="calYillikNokta"/> : null}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // ── AYLIK GÖRÜNÜM ──
  const kutular = [];
  for (let i = 0; i < baslangic; i++) kutular.push(<div key={"b"+i} className="emptyDay"/>);
  const renderDayMarker = (kayit, dateKey, dayIndex) => {
    const multiDay = kayit.type === "event" && normalizeDateKey(kayit.endDate) > normalizeDateKey(kayit.tarih);
    if (multiDay) {
      const startsHere = dateKey === kayit.tarih;
      const endsHere = dateKey === kayit.endDate;
      return (
        <span
          key={`${kayit.type}-${kayit.id}`}
          className={`calendarProcessBand ${(startsHere || dayIndex === 0) ? "start" : ""} ${(endsHere || dayIndex === 6) ? "end" : ""}`}
          style={{ "--event-color": kayit.renk }}
          title={`${kayit.baslik}: ${kayit.tarih} – ${kayit.endDate}`}
          aria-label={`${kayit.baslik}: ${kayit.tarih} – ${kayit.endDate}`}
        >
          {(startsHere || dayIndex === 0) && <span>{kayit.baslik}</span>}
        </span>
      );
    }
    const markerClass = `calendarDayMarker ${kayit.type === "birthday" ? "birthday" : kayit.type === "task" ? "task" : kayit.type === "externalEvent" ? "external" : "event"} ${kayit.tamamlandi ? "done" : ""}`;
    const markerStyle = kayit.type === "event"
      ? { backgroundColor: kayit.renk }
      : { backgroundColor: kayit.renk, color: "#fff" };
    const markerTitle = kayit.type === "birthday" ? `🎂 ${kayit.kisiAd || kayit.baslik}` : kayit.type === "task" ? `📌 ${kayit.baslik}` : kayit.baslik;

    return (
      <span
        key={`${kayit.type}-${kayit.id}`}
        className={markerClass}
        style={markerStyle}
        title={markerTitle}
        aria-label={markerTitle}
      >
        {kayit.type === "birthday" ? "🎂" : kayit.type === "task" ? "📌" : kayit.type === "externalEvent" ? "↗" : ""}
      </span>
    );
  };

  for (let g = 1; g <= toplamGun; g++) {
    const key = tarihKey(new Date(yil, ay, g));
    const isBugun = key === tarihKey(bugun);
    const kayitlar = gunKayitlari(g);
    const gorunen  = kayitlar.slice(0, 4);
    const kalan    = kayitlar.length - gorunen.length;
    kutular.push(
      <div key={g} className={`dayBox ${isBugun ? "today" : ""} ${seciliGun?.tarih === key ? "selected" : ""}`}
        role="button"
        tabIndex="0"
        aria-label={`${okunakli(key)}, ${kayitlar.length} ${calText.dayEventsCount}`}
        onKeyDown={(event) => {
          if (event.key !== "Enter" && event.key !== " ") return;
          event.preventDefault();
          setSeciliGun({ tarih: key, gun: g, kayitlar }); setFormAcik(false); setDuzenle(null);
        }}
        onClick={() => { setSeciliGun({ tarih: key, gun: g, kayitlar }); setFormAcik(false); setDuzenle(null); }}>
        <div className="dayTop"><span className="dayNumber">{g}</span></div>
        <div className="dayEvents dayMarkers">
          {gorunen.map(kayit => renderDayMarker(kayit, key, (new Date(yil, ay, g).getDay() + 6) % 7))}
          {kalan > 0 && <div className="moreEvents">+{kalan}</div>}
        </div>
      </div>
    );
  }

  return (
    <div className="pageContent calPageContent">
      <div className="calHeader">
        <button className="calNavBtn" onClick={() => ayDegistir(-1)} aria-label={lang === "en" ? "Previous month" : "Önceki ay"}>‹</button>
        <div className="calHeaderCenter">
          <button type="button" className="calHeaderTitle calHeaderTitleClickable" onClick={() => setYillikGorunum(true)} title={t("calendar.goToYearly")}>
            {AYLAR[ay]} {yil}
          </button>
          <button type="button" className="calTodayBtn" onClick={() => { const now = new Date(); setTarih(new Date(now.getFullYear(), now.getMonth(), 1)); setSeciliGun(null); }}>
            {calText.todayAction}
          </button>
        </div>
        <button className="calNavBtn" onClick={() => ayDegistir(1)} aria-label={lang === "en" ? "Next month" : "Sonraki ay"}>›</button>
      </div>
      <div
        className={`calendarSwipeArea ${monthTransition ? `calendarSwipeArea--${monthTransition}` : ""}`}
        onTouchStart={handleMonthSwipeStart}
        onTouchEnd={handleMonthSwipeEnd}
      >
        <div key={`month-${yil}-${ay}`} className="calendarMonthPane">
          <div className="weekRow">
            {GUNLER.map(g => <div key={g} className="weekDay">{g}</div>)}
          </div>
          <div className="calendarGrid">{kutular}</div>
        </div>
      </div>

      <div className="calUpcoming">
        <div className="calUpcomingHeader">
          <div className="calUpcomingTitle">{t("calendar.upcomingTitle")}</div>
          {permissions?.actions?.addCalendarEvent && (
            <button type="button" className="calUpcomingAdd" onClick={() => { const now = new Date(); const key = tarihKey(now); setTarih(new Date(now.getFullYear(), now.getMonth(), 1)); setSeciliGun({ tarih: key, gun: now.getDate(), kayitlar: tumKayitlar.filter(k => kayitGunundeMi(k, key)) }); setFormAcik(true); setDuzenle(null); setKisiSecAcik(false); setYeniKisiler(prev => prev.length ? prev : (user?.uid && safeUyeler.some(u => u.uid === user.uid) ? [user.uid] : (safeUyeler[0]?.uid ? [safeUyeler[0].uid] : []))); }}>
              <span aria-hidden="true">＋</span>{calText.addEventAction}
            </button>
          )}
        </div>
        {(bugunKayit.length === 0 && yarinKayit.length === 0 && haftaKayit.length === 0) ? (
          <div className="calendarEmptyPremium" role="status" aria-live="polite">
            <img
              className="calendarEmptyPremiumImage"
              src={takvimAsset}
              alt=""
              aria-hidden="true"
              loading="eager"
              decoding="sync"
              fetchPriority="high"
              draggable="false"
            />
            <h3 className="calendarEmptyPremiumTitle">
              {lang === "en" ? "No upcoming events" : "Yaklaşan etkinlik yok"}
            </h3>
            <p className="calendarEmptyPremiumText">
              {lang === "en"
                ? "Add a plan to your calendar and keep your family's special moments organized together."
                : "Takviminize bir plan ekleyin, ailenizin özel anlarını birlikte düzenli tutun."}
            </p>
            {permissions?.actions?.addCalendarEvent && (
              <button type="button" className="calendarEmptyAddBtn" onClick={() => { const now = new Date(); const key = tarihKey(now); setSeciliGun({ tarih: key, gun: now.getDate(), kayitlar: [] }); setFormAcik(true); setYeniKisiler(prev => prev.length ? prev : (user?.uid ? [user.uid] : [])); }}>
                ＋ {calText.addEventAction}
              </button>
            )}
          </div>
        ) : ([
          { lbl: t("calendar.labelToday"),    list: bugunKayit },
          { lbl: t("calendar.labelTomorrow"),  list: yarinKayit },
          { lbl: t("calendar.labelThisWeek"),  list: haftaKayit.slice(0, 5) },
        ].map(({ lbl, list }) => (
          <div key={lbl} className="calUpcomingSection">
            <div className="calUpcomingLabel">{lbl}</div>
            {list.length === 0
              ? <div className="calUpcomingEmpty">{t("calendar.noRecord")}</div>
              : list.map(k => <UpcItem key={k.id} k={k}/>)
            }
          </div>
        )))}
      </div>

      {seciliGun && (
        <div className="modalOverlay" onClick={() => setSeciliGun(null)}>
          <div className="darkModal calModalCard" onClick={e => e.stopPropagation()}>
            <div className="calModalGrabber" aria-hidden="true" />
            <div className="calModalHeader">
              <div className="calModalHeading">
                <h2 className="calModalDate">{okunakli(seciliGun.tarih)}</h2>
                <span className="calModalCount">{seciliGun.kayitlar.length} {calText.dayEventsCount}</span>
              </div>
              <div className="calModalHeaderActions">
                {permissions?.actions?.addCalendarEvent && !formAcik && seciliGun.kayitlar.length > 0 && (
                  <button className="calAddBtn" onClick={() => { setFormAcik(true); setDuzenle(null); setKisiSecAcik(false); setYeniKisiler(prev => prev.length ? prev : (user?.uid && safeUyeler.some(u => u.uid === user.uid) ? [user.uid] : (safeUyeler[0]?.uid ? [safeUyeler[0].uid] : []))); }}>{t("calendar.addBtn")}</button>
                )}
                <button type="button" className="calModalCloseIcon" onClick={() => { setSeciliGun(null); setFormAcik(false); setDuzenle(null); }} aria-label={t("calendar.closeBtn")}>×</button>
              </div>
            </div>

            {formAcik && (
              <div className="calForm">
                <input className="darkInput" placeholder={t("calendar.eventTitle")} value={yeniBaslik} onChange={e => setYeniBaslik(e.target.value)}/>
                <div className="inputLabelGroup">
                  <label className="darkLabel" htmlFor="calendar-new-event-time">{t("calendar.time")} <span className="calOptionalHint">({lang === "en" ? "optional" : "isteğe bağlı"})</span></label>
                  <input id="calendar-new-event-time" className="darkInput" type="time" value={yeniSaat} onChange={e => setYeniSaat(e.target.value)}/>
                </div>
                <div className="inputLabelGroup">
                  <label className="darkLabel">{t("calendar.assignee")}</label>
                  <div className={`calMultiSelectBox ${kisiSecAcik ? "open" : "closed"}`} role="group" aria-label={t("calendar.assignee")}>
                    <button
                      type="button"
                      className="calMultiToggle"
                      onClick={() => setKisiSecAcik(prev => !prev)}
                      aria-expanded={kisiSecAcik}
                    >
                      <span className="calMultiToggleLeft">
                        <span className="calMultiToggleTitle">{calText.assigneeSelectTitle}</span>
                        <span className="calMultiToggleSummary">{kisiSecimOzeti}</span>
                      </span>
                      <span className="calMultiToggleAvatars" aria-hidden="true">
                        {seciliUyeler.slice(0, 3).map(u => (
                          <span className="calMultiToggleAvatar" key={u.uid}><UyeAvatar uye={u} small /></span>
                        ))}
                        {seciliUyeler.length > 3 && <span className="calMultiToggleMore">+{seciliUyeler.length - 3}</span>}
                      </span>
                      <span className={`calMultiToggleChevron ${kisiSecAcik ? "open" : ""}`}>⌄</span>
                    </button>

                    {kisiSecAcik && (
                      <div className="calMultiDropdown">
                        <button
                          type="button"
                          className={`calMultiOption all ${yeniKisiler.length === siraliUyeler.length && siraliUyeler.length > 0 ? "active" : ""}`}
                          onClick={() => setYeniKisiler(prev => prev.length === siraliUyeler.length ? [] : siraliUyeler.map(u => u.uid))}
                        >
                          <span className="calMultiCheck">{yeniKisiler.length === siraliUyeler.length && siraliUyeler.length > 0 ? "✓" : ""}</span>
                          <span className="calMultiText">
                            <strong>{calText.allFamily}</strong>
                            <small>{calText.allFamilyDesc}</small>
                          </span>
                          <span className="calMultiCount">{siraliUyeler.length}</span>
                        </button>
                        <div className="calMultiMemberList">
                          {siraliUyeler.map(u => {
                            const role = normalizeRole(u);
                            const secili = yeniKisiler.includes(u.uid);
                            return (
                              <button
                                type="button"
                                key={u.uid}
                                className={`calMultiOption ${secili ? "active" : ""}`}
                                onClick={() => setYeniKisiler(prev => secili ? prev.filter(uid => uid !== u.uid) : [...prev, u.uid])}
                              >
                                <span className="calMultiCheck">{secili ? "✓" : ""}</span>
                                <span className="calMultiAvatar"><UyeAvatar uye={u} /></span>
                                <span className="calMultiText">
                                  <strong>{u.ad} {u.soyad}</strong>
                                  <small>{roleLabel(role)}</small>
                                </span>
                              </button>
                            );
                          })}
                        </div>
                        <button type="button" className="calMultiCloseBtn" onClick={() => setKisiSecAcik(false)}>{calText.collapse}</button>
                      </div>
                    )}
                  </div>
                  <div className="calMultiSummary">{yeniKisiler.length} {calText.selected}</div>                </div>
                <div className="inputLabelGroup">
                  <label className="darkLabel">{t("common.category")}</label>
                  <select className="darkInput" value={yeniKategori} onChange={e => setYeniKategori(e.target.value)}>
                    {kategoriSecenekleri.map(({ value, label }) => <option key={value} value={value}>{label}</option>)}
                  </select>
                </div>
                <div className="inputLabelGroup calDurationField">
                  <label className="darkLabel">{calText.duration}</label>
                  <div className="calDurationOptions" role="group" aria-label={calText.duration}>
                    {[
                      ["single", calText.durationSingle],
                      ["days3", calText.duration3],
                      ["days7", calText.duration7],
                      ["days10", calText.duration10],
                      ["month", calText.durationMonth],
                      ["custom", calText.durationCustom],
                    ].map(([value, label]) => (
                      <button
                        type="button"
                        key={value}
                        className={`calDurationChip ${yeniSureTipi === value ? "active" : ""}`}
                        onClick={() => {
                          setYeniSureTipi(value);
                          if (value === "custom") {
                            const selected = normalizeDateKey(seciliGun.tarih);
                            setYeniSureBaslangic(prev => prev || selected);
                            setYeniSureBitis(prev => prev && prev >= selected ? prev : selected);
                          }
                        }}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  {yeniSureTipi === "custom" && (
                    <div className="calDateRangePicker">
                      <label>
                        <span>{calText.rangeStart}</span>
                        <input
                          className="darkInput"
                          type="date"
                          value={yeniSureBaslangic || seciliGun.tarih}
                          onChange={(event) => {
                            const nextStart = event.target.value;
                            setYeniSureBaslangic(nextStart);
                            setYeniSureBitis(prev => !prev || prev < nextStart ? nextStart : prev);
                          }}
                        />
                      </label>
                      <span className="calDateRangeArrow" aria-hidden="true">→</span>
                      <label>
                        <span>{calText.rangeEnd}</span>
                        <input
                          className="darkInput"
                          type="date"
                          min={yeniSureBaslangic || seciliGun.tarih}
                          value={yeniSureBitis || yeniSureBaslangic || seciliGun.tarih}
                          onChange={(event) => setYeniSureBitis(event.target.value)}
                        />
                      </label>
                    </div>
                  )}
                </div>
                <div className="inputLabelGroup calRecurrenceField">
                  <label className="darkLabel">{calText.recurrence}</label>
                  <select className="darkInput" value={yeniTekrar} onChange={e => setYeniTekrar(e.target.value)}>
                    <option value="none">{calText.recurrenceNone}</option>
                    <option value="weekly">{calText.recurrenceWeekly}</option>
                    <option value="monthly">{calText.recurrenceMonthly}</option>
                    <option value="quarterly">{calText.recurrenceQuarterly}</option>
                    <option value="semiannual">{calText.recurrenceSemiannual}</option>
                    <option value="yearly">{calText.recurrenceYearly}</option>
                  </select>
                  {yeniTekrar !== "none" && <small className="calRecurrenceHint">{calText.recurrenceLimit}</small>}
                </div>
                <ReminderWarning message={reminderAktif ? reminderError : ""} />
                <ReminderPanel
                  aktif={reminderAktif}  setAktif={setReminderAktif}
                  tarih={reminderTarih}  setTarih={setReminderTarih}
                  saat={reminderSaat}    setSaat={setReminderSaat}
                  tekrar={reminderTekrar} setTekrar={setReminderTekrar}
                  mod="datetime"
                  etiket={t("calendar.reminder")}
                />
                <div className="calFormBtns">
                  <AsyncActionButton
                    className={`primaryButton ${etkinlikKaydediliyor ? "calSavingButton" : ""}`}
                    onAction={etkinlikKaydet}
                    externalBusy={etkinlikKaydediliyor}
                    disabled={etkinlikKaydediliyor}
                  >
                    {t("calendar.save")}
                  </AsyncActionButton>
                  <button className="darkSecondaryBtn" disabled={etkinlikKaydediliyor} onClick={() => { setFormAcik(false); setKisiSecAcik(false); setYeniKisiler([]); setYeniSureTipi("single"); setYeniSureBaslangic(""); setYeniSureBitis(""); }}>{t("common.cancel")}</button>
                </div>
              </div>
            )}

            {duzenle && (
              <div className="calForm">
                <div className="calFormSubTitle">{t("calendar.editTitle")}</div>
                <input className="darkInput" placeholder={t("calendar.eventTitle")} value={dBaslik} onChange={e => setDBaslik(e.target.value)}/>
                <div className="inputLabelGroup">
                  <label className="darkLabel" htmlFor="calendar-edit-event-time">{t("calendar.time")} <span className="calOptionalHint">({lang === "en" ? "optional" : "isteğe bağlı"})</span></label>
                  <input id="calendar-edit-event-time" className="darkInput" type="time" value={dSaat} onChange={e => setDSaat(e.target.value)}/>
                </div>
                <div className="inputLabelGroup">
                  <label className="darkLabel">{t("calendar.assignee")}</label>
                  <select className="darkInput" value={dKisi} onChange={e => setDKisi(e.target.value)}>
                    <option value="">{t("calendar.select")}</option>
                    <option value={ALL_FAMILY_CALENDAR_UID}>{calText.allFamily}</option>
                    {siraliUyeler.map(u => <option key={u.uid} value={u.uid}>{u.ad} {u.soyad}</option>)}
                  </select>
                </div>
                <div className="inputLabelGroup">
                  <label className="darkLabel">{t("common.category")}</label>
                  <select className="darkInput" value={dKategori} onChange={e => setDKategori(e.target.value)}>
                    {kategoriSecenekleri.map(({ value, label }) => <option key={value} value={value}>{label}</option>)}
                  </select>
                </div>
                <div className="inputLabelGroup">
                  <label className="darkLabel">{calText.rangeEnd}</label>
                  <input
                    className="darkInput"
                    type="date"
                    min={normalizeDateKey(duzenle.tarih)}
                    value={dEndDate || normalizeDateKey(duzenle.tarih)}
                    onChange={e => setDEndDate(e.target.value)}
                  />
                </div>
                <div className="calFormBtns">
                  <button className="primaryButton" onClick={etkinlikGuncelle}>{t("calendar.editUpdate")}</button>
                  <button className="darkSecondaryBtn" onClick={() => setDuzenle(null)}>{t("common.cancel")}</button>
                </div>
              </div>
            )}

            {seciliGun.kayitlar.length === 0
              ? <div className="calEmptyState"><span className="calEmptyStateIcon" aria-hidden="true">＋</span><p className="calEmpty">{calText.noEventsHint}</p>{permissions?.actions?.addCalendarEvent && !formAcik && <button type="button" className="calendarEmptyAddBtn calEmptyStateAdd" onClick={() => { setFormAcik(true); setDuzenle(null); setKisiSecAcik(false); setYeniKisiler(prev => prev.length ? prev : (user?.uid && safeUyeler.some(u => u.uid === user.uid) ? [user.uid] : (safeUyeler[0]?.uid ? [safeUyeler[0].uid] : []))); }}>＋ {calText.addEventAction}</button>}</div>
              : seciliGun.kayitlar.map(kayit => (
                <div key={`${kayit.type}-${kayit.id}`} className="calDetailItem" style={{ "--event-color": kayit.renk }}>
                  <div className="calDetailHeader">
                    <div className="userDot" style={{ background: kayit.renk }}/>
                    <span className="userName">{kayit.type === "externalEvent" ? (kayit.sourceTitle || (lang === "en" ? "Device calendar" : "Cihaz takvimi")) : (kayit.kisiAd || "?")}</span>
                    {kayit.tamamlandi && <span className="calDoneBadge">✓</span>}
                    {kayit.type === "event" && normalizeDateKey(kayit.endDate) > normalizeDateKey(kayit.tarih) && seciliGun.tarih > kayit.tarih && seciliGun.tarih <= kayit.endDate && (
                      <span className="calOngoingBadge">{calText.ongoing}</span>
                    )}
                  </div>
                  <div className="calDetailTitle">{kayit.type === "task" ? "📌 " : kayit.type === "birthday" ? "🎂 " : kayit.type === "externalEvent" ? "↗ " : ""}{kayit.baslik}</div>
                  <div className="calDetailMeta">
                    {calendarCategoryLabel(kayit.kategori, kayit.kategoriCanonical)}{kayit.saat ? ` · ${kayit.saat}` : ""}
                    {kayit.type === "event" && normalizeDateKey(kayit.endDate) > normalizeDateKey(kayit.tarih) ? ` · ${okunakli(kayit.tarih)} – ${okunakli(kayit.endDate)}` : ""}
                    {kayit.type === "externalEvent" ? ` · ${lang === "en" ? "Read only" : "Salt okunur"}` : ""}
                  </div>
                  {(duzenlenebilir(kayit) || silebilir(kayit)) && (
                    <div className="calDetailActions">
                      {duzenlenebilir(kayit) && <button className="calEditBtn" onClick={() => duzenleBaslat(kayit)}>{t("calendar.editBtn")}</button>}
                      {silebilir(kayit)      && <button className="calDelBtn"  onClick={() => etkinlikSil(kayit)}>{t("calendar.deleteBtn")}</button>}
                    </div>
                  )}
                  {kayit.type === "birthday" && typeof onBirthdayClick === "function" && (
                    <div className="calDetailActions">
                      <button className="calEditBtn" onClick={() => {
                        setSeciliGun(null);
                        setFormAcik(false);
                        setDuzenle(null);
                        onBirthdayClick(kayit);
                      }}>
                        {kayit.kisiUid === user?.uid
                          ? (lang === "en" ? "View celebration" : "Kutlamaları gör")
                          : (lang === "en" ? "Send birthday wish" : "Doğum gününü kutla")}
                      </button>
                    </div>
                  )}
                  {kayit.type === "externalEvent" && (
                    <div className="calDetailActions">
                      <button
                        type="button"
                        className="calEditBtn"
                        disabled={importingExternalId === kayit.id || importedExternalIds.has(kayit.id)}
                        onClick={() => hariciEtkinligiAileyeAktar(kayit)}
                      >
                        {importedExternalIds.has(kayit.id)
                          ? (lang === "en" ? "Copied" : "Aktarıldı")
                          : importingExternalId === kayit.id
                            ? (lang === "en" ? "Copying…" : "Aktarılıyor…")
                            : (lang === "en" ? "Copy to family calendar" : "Aile takvimine aktar")}
                      </button>
                    </div>
                  )}
                </div>
              ))
            }
            {!formAcik && !duzenle && <button className="darkSecondaryBtn calModalBottomClose" onClick={() => { setSeciliGun(null); setFormAcik(false); setDuzenle(null); }}>{t("calendar.closeBtn")}</button>}
          </div>
        </div>
      )}
    </div>
  );
}

export default CalendarPage;
